import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { usePromoCatalog } from "../../hooks/usePromoCatalog";
import {
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_BRANDS_OPTION,
  PROMO_ALL_CLASSES_ID,
  PROMO_DEFAULT_MANUFACTURER,
  PROMO_HISTORICAL_DEFAULT_YEAR,
} from "../../services/promoSimulatorService";
import {
  WEEK_FILTER_MIN,
  WEEK_FILTER_MAX,
  ALL_CALENDAR_WEEKS,
  HISTORICAL_YEAR_OPTIONS,
} from "./helpers";

export function PerformanceFilterBar({ filters, setFilters, sx: sxProp }) {
  const {
    manufacturers,
    apiBrands,
    apiClasses,
    brandOptions,
    classOptions,
    manufacturersLoading,
    brandsLoading,
    classesLoading,
  } = usePromoCatalog(filters.manufacturer, filters.brand, filters.class);

  useEffect(() => {
    if (brandsLoading || brandOptions.length === 0) return;
    setFilters((f) => {
      if (f.brand && brandOptions.includes(f.brand)) return f;
      return { ...f, brand: PROMO_ALL_BRANDS_ID };
    });
  }, [brandOptions, brandsLoading, setFilters]);

  useEffect(() => {
    if (classesLoading || classOptions.length === 0) return;
    setFilters((f) => {
      if (f.class && classOptions.includes(f.class)) return f;
      return { ...f, class: classOptions[0] };
    });
  }, [classOptions, classesLoading, setFilters]);

  const handleManufacturerChange = (e) => {
    setFilters((f) => ({
      ...f,
      manufacturer: e.target.value,
      brand: PROMO_ALL_BRANDS_ID,
      class: PROMO_ALL_CLASSES_ID,
    }));
  };

  const handleReset = () =>
    setFilters({
      manufacturer: PROMO_DEFAULT_MANUFACTURER,
      brand: PROMO_ALL_BRANDS_ID,
      class: PROMO_ALL_CLASSES_ID,
      startWeek: WEEK_FILTER_MIN,
      endWeek: WEEK_FILTER_MAX,
      year: PROMO_HISTORICAL_DEFAULT_YEAR,
    });

  const year = Number(filters.year ?? PROMO_HISTORICAL_DEFAULT_YEAR);
  const weekStart = Number(filters.startWeek ?? WEEK_FILTER_MIN);
  const weekEnd = Number(filters.endWeek ?? WEEK_FILTER_MAX);

  const handleStartWeekChange = (e) => {
    const v = Number(e.target.value);
    setFilters((f) => ({
      ...f,
      startWeek: v,
      endWeek: Math.max(v, Number(f.endWeek ?? WEEK_FILTER_MAX)),
    }));
  };

  const handleEndWeekChange = (e) => {
    const v = Number(e.target.value);
    setFilters((f) => ({
      ...f,
      endWeek: v,
      startWeek: Math.min(v, Number(f.startWeek ?? WEEK_FILTER_MIN)),
    }));
  };

  const getBrandLabel = (id) => {
    if (id === PROMO_ALL_BRANDS_ID) return PROMO_ALL_BRANDS_OPTION.label;
    const found = apiBrands.find((b) => b.id === id);
    return found ? found.label : id;
  };

  const getClassLabel = (id) => {
    const found = apiClasses.find((c) => c.id === id);
    return found ? found.label : id;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        mb: 3,
        borderRadius: 2,
        border: "1px solid #E8ECF0",
        bgcolor: "#FAFBFC",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        ...sxProp,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 0.5 }}>
          <FilterListOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary" noWrap>
            Filters
          </Typography>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 160 }} disabled={manufacturersLoading}>
          <InputLabel sx={{ fontSize: 13 }}>Manufacturer</InputLabel>
          <Select
            label="Manufacturer"
            value={
              manufacturers.includes(filters.manufacturer)
                ? filters.manufacturer
                : manufacturers[0] ?? PROMO_DEFAULT_MANUFACTURER
            }
            onChange={handleManufacturerChange}
            sx={{ fontSize: 13 }}
          >
            {manufacturers.map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }} disabled={brandsLoading}>
          <InputLabel sx={{ fontSize: 13 }}>Brand</InputLabel>
          <Select
            label="Brand"
            value={brandOptions.includes(filters.brand) ? filters.brand : PROMO_ALL_BRANDS_ID}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                brand: e.target.value,
                class: PROMO_ALL_CLASSES_ID,
              }))
            }
            sx={{ fontSize: 13 }}
          >
            {brandsLoading ? (
              <MenuItem value={PROMO_ALL_BRANDS_ID} sx={{ fontSize: 13 }}>
                {PROMO_ALL_BRANDS_OPTION.label}
              </MenuItem>
            ) : (
              brandOptions.map((id) => (
                <MenuItem key={id} value={id} sx={{ fontSize: 13 }}>
                  {getBrandLabel(id)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 170 }}
          disabled={classesLoading || classOptions.length === 0}
        >
          <InputLabel sx={{ fontSize: 13 }}>Class</InputLabel>
          <Select
            label="Class"
            value={classOptions.includes(filters.class) ? filters.class : ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, class: e.target.value || null }))
            }
            sx={{ fontSize: 13 }}
          >
            {classOptions.map((id) => (
              <MenuItem key={id} value={id} sx={{ fontSize: 13 }}>
                {getClassLabel(id)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel sx={{ fontSize: 13 }}>Year</InputLabel>
          <Select
            label="Year"
            value={year}
            onChange={(e) =>
              setFilters((f) => ({ ...f, year: Number(e.target.value) }))
            }
            sx={{ fontSize: 13 }}
          >
            {HISTORICAL_YEAR_OPTIONS.map((y) => (
              <MenuItem key={`ph-filter-year-${y}`} value={y} sx={{ fontSize: 13 }}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: 13 }}>Start week</InputLabel>
          <Select
            label="Start week"
            value={weekStart}
            onChange={handleStartWeekChange}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            sx={{ fontSize: 13 }}
          >
            {ALL_CALENDAR_WEEKS.map((w) => (
              <MenuItem key={`ph-filter-ws-${w}`} value={w} sx={{ fontSize: 13 }}>
                Week {w}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: 13 }}>End week</InputLabel>
          <Select
            label="End week"
            value={weekEnd}
            onChange={handleEndWeekChange}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            sx={{ fontSize: 13 }}
          >
            {ALL_CALENDAR_WEEKS.map((w) => (
              <MenuItem key={`ph-filter-we-${w}`} value={w} sx={{ fontSize: 13 }}>
                Week {w}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="text"
          startIcon={<RestartAltIcon fontSize="small" />}
          onClick={handleReset}
          sx={{
            textTransform: "none",
            fontSize: 13,
            color: "text.secondary",
            ml: "auto",
          }}
        >
          Reset
        </Button>
      </Stack>
    </Paper>
  );
}
